import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import Link from 'next/link'

interface ExpertInAreaData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  image?: {
    alt: string;
    image_file: {
      image: {
        id: string;
        filename_download: string;
      };
    };
  };
  content_collection?: {
    id: string;
    site_content_collections_id: {
      collection: {
        collection: string;
        item: {
          title: string;
          text: string;
        };
      }[];
    };
  }[];
}

export const fields = [
  // headings
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // button
  "button.label",
  "button.href",

  // image
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  
  // content collection / stats
  "content_collection.id",
  "content_collection.site_content_collections_id.collection.collection",
  "content_collection.site_content_collections_id.collection.item:site_content.title",
  "content_collection.site_content_collections_id.collection.item:site_content.text"
] as const;

interface ExpertInAreaProps {
  data?: ExpertInAreaData;
}

export default function ExpertInArea({ data }: ExpertInAreaProps) {
  // Extract the stats from content collection
  const statsItems = data?.content_collection?.map(collection => {
    const items = collection?.site_content_collections_id?.collection || [];
    
    // Find separate site_content items for value (title) and text
    const valueItem = items.find(item => 
      item.collection === "site_content" && 
      item.item.title && 
      !item.item.text
    )?.item;
    
    const textItem = items.find(item => 
      item.collection === "site_content" && 
      item.item.text && 
      !item.item.title
    )?.item;
    
    // Fallback to any site_content if needed
    const fallbackItem = items.find(item => 
      item.collection === "site_content"
    )?.item;
    
    return {
      value: valueItem?.title || fallbackItem?.title || "",
      text: textItem?.text || fallbackItem?.text || ""
    };
  }) || [];
  
  // Get image src or fallback
  const imageSrc = data?.image?.image_file?.image?.id && data?.image?.image_file?.image?.filename_download
    ? getReadableImageUrl(data.image.image_file.image.id, data.image.image_file.image.filename_download)
    : "/images/bader-with-patient.png";
  
  const imageAlt = data?.image?.alt || "Doctor consultation";

  return (
    <section className="relative w-full overflow-hidden bg-[#F6F6F6]">
      {/* Desktop and Mobile Hero Section */}
      <div className="mx-auto pt-8 pb-8 xl:pb-16">
        <div className="relative lg:flex lg:items-center">
          {/* Image Container */}
          <div className="relative w-full lg:w-1/2">
            <div className="relative h-[30.5625rem] lg:h-[40rem] overflow-hidden">
              <div className="absolute left-0 w-full h-full lg:rounded-tr-[25rem] lg:rounded-br-[25rem] overflow-hidden">
                <Image
                  src={imageSrc}
                  alt={imageAlt}
                  fill
                  className="object-cover object-center"
                  priority
                  sizes="(max-width: 768px) 100vw, 50vw"
                  quality={90}
                />
              </div>
            </div>
          </div>

          {/* Content Container */}
          <div className="relative px-5 lg:px-0 lg:w-1/2 lg:pl-20 lg:pr-28 z-10 -mt-[4rem] lg:mt-0">
            <div className="absolute -top-0 left-0 w-full lg:hidden -z-10">
              <Image
                src="/images/top-ellipse.svg"
                alt="Decorative top shape"
                width={393}
                height={39}
                className="w-full h-[5rem] object-cover"
                quality={90}
              />
            </div>

            <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary mt-8 pt-16 lg:pt-0">
              {data?.heading?.title || ""}
            </h2>

            <div 
              className="text-[1rem] font-normal leading-normal md:text-xl text-primary font-greycliff mt-8 md:leading-[1.35] tracking-tight [&>p]:mb-4 [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl"
              dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
            />

            <div className="grid grid-cols-2 gap-8 mt-8">
              {statsItems.map((stat, index) => (
                <div key={index}>
                  <span className="text-[1.5rem] lg:text-[2.5rem] font-greycliff text-primary font-light block leading-[1]">{stat.value}</span>
                  <div 
                    className="text-primary font-greycliff [&>p]:mb-2 md:text-xl md:font-normal md:leading-normal text-[1rem] font-normal leading-normal [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal md:[&>*]:text-xl"
                    dangerouslySetInnerHTML={sanitizeHTML(stat.text)}
                  />
                </div>
              ))}
            </div>

            {data?.button?.href && data?.button?.label && (
              <Link 
                href={data.button.href || "#"} 
                className="mt-6 lg:mt-11 inline-block w-full lg:w-auto px-12 py-4 bg-primary text-white rounded-full font-greycliff text-xl tracking-tight hover:bg-light hover:text-primary hover:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transition-all"
              >
                {data.button.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  )
} 